/**
 * C01. Streak banner — home page widget.
 *
 * Renders 🔥 + 連續 N 天 + loss-aversion copy when the streak is at risk.
 * Logic-free; just consumes a `StreakSnapshot` produced by
 * `processStreakOnHomeEnter`. Keeping render isolated from state lets us
 * unit-test the banner with synthetic snapshots without going through
 * localStorage or `tickStreakOnLoad`.
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md §C01.
 *
 * Styling: inline (matches existing dayone-banner / my-progress-card pattern).
 * Banner hides itself when `days === 0` — fresh accounts get the Day-1 hook
 * (C05) banner instead; doubling up two big banners on first launch would
 * crowd the kid out of the hero CTAs.
 */

import type { StreakSnapshot } from './streak';

export interface StreakBannerOptions {
  /** Override for deterministic snapshot tests. */
  snapshot: StreakSnapshot;
}

/**
 * Mount the streak banner into `container`. Returns an unmount function the
 * caller may invoke when navigating away from home. Idempotent: calling
 * twice clears the previous render before drawing the new one (so
 * `re-render on profile switch` works without duplicating nodes).
 */
export function renderStreakBanner(
  container: HTMLElement,
  options: StreakBannerOptions,
): () => void {
  // Clear any prior banner mounted in this slot
  container.innerHTML = '';

  const { snapshot } = options;
  // Fresh accounts (days === 0) get nothing — C05 dayone-banner owns first-launch real-estate.
  if (snapshot.days < 1) return () => { /* noop */ };

  const root = document.createElement('div');
  root.className = 'streak-banner';
  root.setAttribute('data-testid', 'streak-banner');
  root.setAttribute('data-streak-days', String(snapshot.days));
  if (snapshot.atRisk) root.setAttribute('data-streak-at-risk', 'true');
  if (snapshot.justCrossedMilestone !== null) {
    root.setAttribute('data-streak-milestone', String(snapshot.justCrossedMilestone));
  }

  root.style.cssText = [
    'display:flex',
    'align-items:center',
    'gap:12px',
    'padding:12px 16px',
    'margin:8px 0',
    'border-radius:14px',
    'background:linear-gradient(135deg,#FFE0B2 0%,#FFAB91 100%)',
    'border:2px solid ' + (snapshot.atRisk ? '#E53935' : '#FB8C00'),
    'box-shadow:0 2px 6px rgba(0,0,0,0.12)',
    'font-family:inherit',
  ].join(';');

  const flame = document.createElement('div');
  flame.className = 'streak-flame';
  flame.textContent = '🔥';
  flame.style.cssText = 'font-size:32px;flex:0 0 auto;line-height:1;';
  if (snapshot.justCrossedMilestone !== null) {
    // Quick CSS-only pulse on milestone — does not block, no animation lib
    flame.style.animation = 'streak-pulse 0.6s ease-out 3';
  }

  const body = document.createElement('div');
  body.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:2px;color:#3E2723;';

  const headline = document.createElement('div');
  headline.className = 'streak-headline';
  headline.style.cssText = 'font-size:18px;font-weight:700;line-height:1.2;';
  headline.textContent = bannerHeadline(snapshot);

  const sub = document.createElement('div');
  sub.className = 'streak-sub';
  sub.style.cssText = 'font-size:13px;line-height:1.3;color:' + (snapshot.atRisk ? '#B71C1C' : '#5D4037');
  sub.textContent = bannerSubline(snapshot);

  body.append(headline, sub);
  root.append(flame, body);
  container.appendChild(root);

  // Lazy-inject the keyframes once per document (idempotent — guarded by id).
  ensurePulseKeyframes();

  return () => { root.remove(); };
}

/**
 * Headline copy:
 *   - At risk      → 「🔥 大俠!連勝快斷了!」(loss aversion)
 *   - Just crossed → 「🔥 N 天連勝!里程碑達成!」
 *   - Steady       → 「🔥 連續修煉 N 天」
 *
 * Exported for unit tests so we can assert on the copy without DOM scraping.
 */
export function bannerHeadline(s: StreakSnapshot): string {
  if (s.atRisk) {
    return `🔥 大俠!${s.days} 天連勝快斷了!`;
  }
  if (s.justCrossedMilestone !== null) {
    return `🎉 ${s.days} 天連勝!里程碑達成!`;
  }
  return `🔥 連續修煉 ${s.days} 天`;
}

/**
 * Sub-copy:
 *   - At risk      → 「再不登入今天就要重來囉!」
 *   - Past 100     → 「🐉 傳說大俠,持續打破紀錄」
 *   - Has next     → 「再 K 天就能解鎖第 N 連勝獎勵」
 *   - Otherwise    → 「歷史最佳:N 天」
 */
export function bannerSubline(s: StreakSnapshot): string {
  if (s.atRisk) {
    return '今天還沒登入答題,再不來就要從 1 開始!';
  }
  if (s.nextMilestone === null) {
    return `🐉 傳說大俠 · 歷史最佳 ${s.longest} 天`;
  }
  if (s.daysToNextMilestone === 0) {
    // Just crossed it — celebrate, no countdown noise.
    return `歷史最佳:${s.longest} 天`;
  }
  return `再 ${s.daysToNextMilestone} 天解鎖 ${s.nextMilestone} 連勝里程碑 · 最佳 ${s.longest} 天`;
}

/** Inject the pulse keyframes once. Idempotent. */
function ensurePulseKeyframes(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('streak-pulse-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'streak-pulse-keyframes';
  style.textContent =
    '@keyframes streak-pulse { 0% { transform:scale(1); } 50% { transform:scale(1.4); } 100% { transform:scale(1); } }';
  document.head.appendChild(style);
}
